import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  AnomalyPolygon,
  AnomalyPolygonsService,
  CustomAnomalyPolygonsViewModel,
} from '@6grain/masofa-monolith-api-client/satellite-indices';
import { FilterOperator } from '@app/utils/filter-operator';
import { translateKeyForAnomalyType } from '@app/pages/crop-monitoring/pages/anomalies/anomaly-type-colors';

export interface ParentAnomalySelectOption {
  id: string;
  name: string;
  /** Kept so labels can be rebuilt on language change. */
  typeKey?: string | null;
  fieldName?: string;
  date?: string;
  /** From anomaly polygon — used to auto-fill bid Field / Season on select. */
  fieldId?: string | null;
  seasonId?: string | null;
  /** Satellite polygon comment — shown as WT15 anomalyComment on select. */
  comment?: string | null;
  /** WKT used to match zip anomaly_description to this parent. */
  polygonJson?: string | null;
}

type SelectQuery = {
  take?: number;
  offset?: number;
  filters?: Array<{
    filterField?: string;
    filterOperator?: number;
    filterValue?: unknown;
  }>;
};

/**
 * Select-paginated adapter over CustomGetAnomalyPolygons for WT15 parent link.
 * Open list: Active satellite only. Load-by-id uses GetById so Hidden (processed) parents still label.
 */
@Injectable({ providedIn: 'root' })
export class SatelliteParentAnomalySelectService {
  constructor(
    private readonly anomalyPolygonsService: AnomalyPolygonsService,
    private readonly translate: TranslateService,
  ) {}

  searchSatelliteParentAnomalies(query: unknown): Observable<ParentAnomalySelectOption[]> {
    const q = (query ?? {}) as SelectQuery;
    const incoming = Array.isArray(q.filters) ? [...q.filters] : [];

    let filters = incoming.filter((f) => {
      const field = (f.filterField ?? '').toLowerCase();
      // Drop label search noise and any prior origin so we append Satellite once.
      return (
        field !== 'name' &&
        field !== 'label' &&
        field !== 'names' &&
        field !== 'anomalyorigin' &&
        field !== 'sourcekind'
      );
    });

    const idEquals = readIdEquals(filters);

    // Edit load-by-id: CustomGet is Active-only, so a processed (Hidden) parent 404s
    // and the dropdown stays on the placeholder. GetById still returns Hidden rows.
    if (idEquals) {
      return this.loadParentByIdEvenIfHidden(idEquals);
    }

    // ProductSourceType AnyOf — same shape that CustomGet returns data for.
    // (AnomalyOrigin=Satellite server-default path was returning empty 404.)
    filters = [
      ...filters,
      {
        filterField: 'ProductSourceType',
        filterOperator: FilterOperator.AnyOf,
        filterValue: [1, 2], // Sentinel2, Landsat
      },
    ];

    return this.anomalyPolygonsService
      .satelliteAnomalyPolygonsCustomGetAnomalyPolygonsPost({
        take: q.take,
        offset: q.offset,
        filters: filters as any,
      })
      .pipe(
        map((rows: CustomAnomalyPolygonsViewModel[]) =>
          (rows ?? [])
            .filter((row) => !!row?.anomalyPolygon?.id)
            .map((row) => this.toSelectOption(row)),
        ),
        // CustomGet returns 404 when empty — treat as no options; rethrow other errors.
        catchError((err: unknown) => {
          if (err instanceof HttpErrorResponse && err.status === 404) {
            return of([]);
          }
          return throwError(() => err);
        }),
      );
  }

  /** Rebuild composed name after language switch (type key is translated again). */
  relabel(option: ParentAnomalySelectOption): ParentAnomalySelectOption {
    return {...option, name: this.formatOption(option)};
  }

  private toSelectOption(row: CustomAnomalyPolygonsViewModel): ParentAnomalySelectOption {
    const ap = row.anomalyPolygon;
    return this.toSelectOptionFromPolygon(ap!, (row.fieldName ?? '').trim() || '');
  }

  private loadParentByIdEvenIfHidden(id: string): Observable<ParentAnomalySelectOption[]> {
    return this.anomalyPolygonsService.satelliteAnomalyPolygonsGetByIdIdGet(id).pipe(
      map((ap: AnomalyPolygon) => (ap?.id ? [this.toSelectOptionFromPolygon(ap)] : [])),
      catchError(() => of([])),
    );
  }

  private toSelectOptionFromPolygon(
    ap: AnomalyPolygon,
    fieldName = '',
  ): ParentAnomalySelectOption {
    const option: ParentAnomalySelectOption = {
      id: String(ap.id),
      name: '',
      typeKey: translateKeyForAnomalyType(ap.anomalyType) ?? null,
      fieldName,
      date: ap.originalDate ? String(ap.originalDate).slice(0, 10) : '',
      fieldId: ap.fieldId != null && ap.fieldId !== '' ? String(ap.fieldId) : null,
      seasonId: ap.seasonId != null && ap.seasonId !== '' ? String(ap.seasonId) : null,
      comment: trimToNull(ap.comment),
      polygonJson: trimToNull(ap.polygonJson),
    };
    option.name = this.formatOption(option);
    return option;
  }

  /**
   * Anomaly-first label (not cadastral-first — that looked like the Field dropdown).
   * Example: "Drought · 2026-08-10"
   */
  private formatOption(option: ParentAnomalySelectOption): string {
    const typeLabel = option.typeKey ? this.translate.instant(option.typeKey) : 'Anomaly';
    const date = option.date?.trim();
    return [typeLabel, date].filter(Boolean).join(' · ');
  }
}

function trimToNull(value: string | null | undefined): string | null {
  if (value == null) {
    return null;
  }
  const text = String(value).trim();
  return text || null;
}

function readIdEquals(
  filters: Array<{filterField?: string; filterOperator?: number; filterValue?: unknown}>,
): string | null {
  const match = filters.find(
    (f) =>
      (f.filterField ?? '').toLowerCase() === 'id' &&
      (f.filterOperator === FilterOperator.Equals || f.filterOperator === 0),
  );
  if (match?.filterValue == null) {
    return null;
  }
  const id = String(match.filterValue).trim();
  return id && id !== '00000000-0000-0000-0000-000000000000' ? id : null;
}
